/*
  Forest Origins — "Build your own grow" set builder (snippets/fw-mix-builder.liquid).
  Up to twelve single kits, repeats allowed, each limited by its own availability and quantity rule.
  setPriceCents() is the single pricing function (all money in integer cents):
    0 kits = 0; 1 kit = that kit's real price; 2 kits = price-two; 3 kits = price-three;
    3+ kits keep the three-kit per-kit rate, with the final total rounded to integer cents.
  Native Shopify automatic discounts enforce the set price on one-time single-kit purchases. "Build a bundle" links ([data-fw-mix-start]) start an empty set with that kit and never
  replace a set the shopper has already built. Idempotent for theme-editor section reloads.
*/
(() => {
  const fill = (template, values) =>
    String(template || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key) => (key in values ? values[key] : match));

  const builders = [];

  function init(root) {
    if (!root || root.dataset.fwReady) return;
    root.dataset.fwReady = 'true';
    root.hidden = false;

    const formatter = (() => {
      try {
        return new Intl.NumberFormat(root.dataset.locale || document.documentElement.lang || 'en-US', {
          style: 'currency',
          currency: root.dataset.currency || 'USD',
        });
      } catch (error) {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
      }
    })();
    const money = (cents) => formatter.format(cents / 100);
    const msg = root.dataset;

    const tiers = {
      two: Math.round(Number(msg.priceTwo) || 0),
      three: Math.round(Number(msg.priceThree) || 0),
    };

    const kits = [...root.querySelectorAll('[data-fw-mix-kit]')].map((element) => ({
      element,
      handle: element.dataset.handle || '',
      variantId: element.dataset.variantId || '',
      name: element.dataset.name || '',
      price: Math.round(Number(element.dataset.price) || 0),
      available: element.dataset.available === 'true',
      max: element.dataset.max === '' ? Number.POSITIVE_INFINITY : Math.max(0, Math.floor(Number(element.dataset.max) || 0)),
      count: 0,
      qty: element.querySelector('[data-fw-mix-qty]'),
      dec: element.querySelector('[data-fw-mix-dec]'),
      inc: element.querySelector('[data-fw-mix-inc]'),
    }));
    const countEl = root.querySelector('[data-fw-mix-count]');
    const linesEl = root.querySelector('[data-fw-mix-lines]');
    const totalEl = root.querySelector('[data-fw-mix-total]');
    const compareEl = root.querySelector('[data-fw-mix-compare]');
    const statusEl = root.querySelector('[data-fw-mix-status]');
    const clearButton = root.querySelector('[data-fw-mix-clear]');
    const shippingEl = root.querySelector('[data-fw-mix-shipping]');
    const shippingTotalEl = root.querySelector('[data-fw-mix-delivered]');
    const savingsTotalEl = root.querySelector('[data-fw-mix-savings-total]');
    const savingsAmountEl = root.querySelector('[data-fw-mix-savings-amount]');
    const savingsScopeEl = root.querySelector('[data-fw-mix-savings-scope]');
    const regularWrapEl = root.querySelector('[data-fw-mix-regular-wrap]');
    const regularEl = root.querySelector('[data-fw-mix-regular]');
    const regularLabelEl = root.querySelector('[data-fw-mix-regular-label]');
    const totalLabelEl = root.querySelector('[data-fw-mix-total-label]');

    const submitButton = root.querySelector('[data-fw-mix-submit]');
    const errorEl = root.querySelector('[data-fw-mix-error]');
    const maxSet = 12;
    let busy = false;
    let cartNeedsReview = false;
    const selected = () => kits.reduce((sum, kit) => sum + kit.count, 0);
    const separateCents = () => kits.reduce((sum, kit) => sum + kit.count * kit.price, 0);

    /** The single pricing function (integer cents). */
    function setPriceCents() {
      const count = selected();
      const separate = separateCents();
      let price;
      if (count <= 1) price = separate;
      else if (count === 2) price = tiers.two;
      else price = Math.round(count * tiers.three / 3);
      return Math.min(price, separate);
    }

    function countText(count) {
      return count === 1 ? msg.msgCountOne : fill(msg.msgCountOther, { count });
    }

    function render(announce = '') {
      const count = selected();
      kits.forEach((kit) => {
        kit.qty.textContent = String(kit.count);
        if (msg.context === 'compare') kit.element.classList.toggle('is-selected', kit.count > 0);
        kit.dec.disabled = busy || kit.count === 0;
        kit.inc.disabled = busy || !kit.available || kit.count >= kit.max || count >= maxSet;
      });

      countEl.textContent = count ? countText(count) : msg.msgEmpty;
      linesEl.replaceChildren(
        ...kits
          .filter((kit) => kit.count > 0)
          .map((kit) => {
            const item = document.createElement('li');
            item.textContent = `${kit.count} × ${kit.name}`;
            return item;
          })
      );

      const price = setPriceCents();
      const separate = separateCents();
      totalEl.textContent = money(price);
      if (regularEl) regularEl.textContent = money(separate);
      if (regularLabelEl) regularLabelEl.textContent = msg.msgRegular || '';
      if (regularWrapEl) regularWrapEl.hidden = count < 2 || separate <= price;
      compareEl.textContent =
        count >= 2 && separate > price
          ? fill(msg.msgSave, { amount: money(separate - price) })
          : '';

      if (shippingEl) {
        const threshold = Math.round(Number(msg.shippingThreshold) || 5000);
        const rate = Math.max(0, Math.round(Number(msg.standardShipping) || 0));
        const isUsd = msg.currency === 'USD';
        const freeShipping = isUsd && count > 0 && price >= threshold;
        const shipping = freeShipping ? 0 : rate;
        if (totalLabelEl) totalLabelEl.textContent = freeShipping || count === 0
          ? msg.msgTotal || '' : msg.msgSubtotal || msg.msgTotal || '';
        shippingEl.textContent = count === 0 ? '' : !isUsd
          ? msg.msgShippingCheckout || ''
          : freeShipping
            ? msg.msgShipping || ''
            : fill(msg.msgShippingPaid, { amount: money(shipping) });
        shippingEl.hidden = count === 0;
        if (savingsTotalEl) {
          const separateShipping = kits.reduce((sum, kit) =>
            sum + kit.count * (kit.price < threshold ? rate : 0), 0);
          // Savings compare regular kit prices only; free shipping is shown on its own line.
          const includesShipping = false;
          const comparison = separate + (includesShipping ? separateShipping : 0);
          const savings = comparison - price;
          const showSavings = count >= 2 && savings > 0;
          savingsTotalEl.hidden = !showSavings;
          compareEl.hidden = showSavings;
          if (regularEl) regularEl.textContent = money(comparison);
          if (regularWrapEl) regularWrapEl.hidden = !showSavings;
          if (regularLabelEl) regularLabelEl.textContent = includesShipping
            ? msg.msgRegularShipping || msg.msgRegular || '' : msg.msgRegular || '';
          if (savingsAmountEl) savingsAmountEl.textContent = money(savings);
          if (savingsScopeEl) {
            savingsScopeEl.textContent = fill(msg.msgSavingsScope, { count, shipping: money(rate) });
            savingsScopeEl.hidden = !includesShipping || !showSavings;
          }
        }
        if (shippingTotalEl) {
          shippingTotalEl.textContent = count > 0 && isUsd && !freeShipping
            ? fill(msg.msgDelivered, { amount: money(price + shipping) }) : '';
          shippingTotalEl.hidden = !shippingTotalEl.textContent;
        }
      }

      if (msg.context === 'compare') {
        const isTrio = kits.length === 3 && kits.every((kit) => kit.count === 1);
        const trioButton = root.querySelector('[data-fw-mix-trio-submit]');
        if (trioButton) trioButton.disabled = !isTrio || trioButton.dataset.available !== 'true' || Number(trioButton.dataset.price) !== price;
        const previewNote = root.querySelector('[data-fw-mix-preview-note]');
        if (previewNote) previewNote.hidden = isTrio && trioButton && !trioButton.disabled;
        root.querySelector('.fc-trio-kit')?.classList.toggle('is-selected', isTrio);
      }
      if (clearButton) clearButton.disabled = busy || count === 0;
      if (submitButton) {
        submitButton.disabled = busy || cartNeedsReview || count === 0 || count > maxSet;
        submitButton.textContent = busy ? msg.msgAdding : msg.msgAddCart;
      }
      if (announce) {
        const summary = count ? `${countText(count)}, ${money(price)}.` : msg.msgEmpty;
        statusEl.textContent = `${announce} ${summary}`;
      }
    }

    function add(kit, { quiet = false } = {}) {
      if (busy || !kit.available || selected() >= maxSet) return false;
      if (kit.count >= kit.max) {
        if (!quiet) render(fill(msg.msgMax, { name: kit.name }));
        return false;
      }
      kit.count += 1;
      if (!quiet) render(`${kit.name}: ${kit.count} ${msg.msgInSet}.`);
      return true;
    }


    submitButton?.addEventListener('click', async () => {
      if (busy || cartNeedsReview || selected() < 1 || selected() > maxSet) return;
      busy = true;
      if (errorEl) { errorEl.textContent = ''; errorEl.hidden = true; }
      render();
      const base = window.Shopify?.routes?.root || '/';
      let attemptedAdd = false;
      try {
        const cartResponse = await fetch(base + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!cartResponse.ok) throw new Error(msg.msgCartError);
        const cart = await cartResponse.json();
        const eligibleIds = new Set(kits.map(kit => kit.variantId));
        const existingCount = cart.items.reduce((sum, item) =>
          sum + (eligibleIds.has(String(item.variant_id)) && !item.selling_plan_allocation ? item.quantity : 0), 0);
        if (existingCount + selected() > maxSet) throw new Error(msg.msgCartLimit);
        const chosen = kits.filter(kit => kit.count > 0);
        for (const kit of chosen) {
          const already = cart.items.filter(item => String(item.variant_id) === kit.variantId)
            .reduce((sum, item) => sum + item.quantity, 0);
          if (!kit.variantId || already + kit.count > kit.max) throw new Error(fill(msg.msgStock, { name: kit.name }));
        }
        attemptedAdd = true;
        const response = await fetch(base + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: chosen.map(kit => ({ id: kit.variantId, quantity: kit.count })) })
        });
        if (!response.ok) throw new Error(msg.msgCartError);
        window.location.assign(base + 'cart');
      } catch (error) {
        cartNeedsReview = attemptedAdd;
        busy = false;
        if (errorEl) {
          errorEl.textContent = error.message || msg.msgCartError;
          errorEl.hidden = false;
          const link = document.createElement('a');
          link.href = base + 'cart';
          link.textContent = msg.msgViewCart;
          errorEl.append(' ', link);
        }
        render();
      }
    });

    kits.forEach((kit) => {
      kit.inc.addEventListener('click', () => {
        add(kit);
        if (kit.inc.disabled && document.activeElement === kit.inc && !kit.dec.disabled) kit.dec.focus();
      });
      kit.dec.addEventListener('click', () => {
        if (busy || kit.count === 0) return;
        kit.count -= 1;
        render(`${kit.name}: ${kit.count} ${msg.msgInSet}.`);
        if (kit.dec.disabled && document.activeElement === kit.dec && !kit.inc.disabled) kit.inc.focus();
      });
    });

    root.querySelector('[data-fw-mix-all]')?.addEventListener('click', () => {
      if (busy) return;
      if (kits.some((kit) => !kit.available || kit.max < 1)) {
        render('One or more varieties are unavailable.');
        return;
      }
      kits.forEach((kit) => { kit.count = 1; });
      render('One of each variety selected.');
    });

    clearButton?.addEventListener('click', () => {
      if (busy) return;
      kits.forEach((kit) => {
        kit.count = 0;
      });
      render(msg.msgEmpty);
      kits.find((kit) => !kit.inc.disabled)?.inc.focus();
    });

    // Product page: start with this page's kit, so the builder reads as "this kit plus…".
    if (msg.context === 'product') {
      const current = kits.find((kit) => kit.element.classList.contains('is-current'));
      if (current) add(current, { quiet: true });
    }
    render();

    builders.push({
      /** Start an empty set with a kit (never replaces an existing set). */
      start(handle) {
        if (selected() > 0) return;
        const kit = kits.find((item) => item.handle === handle);
        if (kit && add(kit, { quiet: true })) render(fill(msg.msgStarted, { name: kit.name }));
      },
    });
  }

  const run = (scope = document) => scope.querySelectorAll('[data-fw-mix]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => run());
  else run();
  document.addEventListener('shopify:section:load', (event) => run(event.target));

  let bundleTrigger = null;
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const opener = event.target.closest('[data-fw-open-bundle]');
    const closer = event.target.closest('[data-fw-close-bundle]');
    const dialog = document.getElementById('FwProductBundle');
    if (!(dialog instanceof HTMLDialogElement)) return;
    if (opener) { bundleTrigger = opener; dialog.showModal(); }
    if (closer) dialog.close();
    if (event.target === dialog) { const box = dialog.getBoundingClientRect(); if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close(); }
  });
  document.getElementById('FwProductBundle')?.addEventListener('close', () => bundleTrigger?.focus());

  // "Build a bundle" on the home product panels: build on this page.
  document.addEventListener('click', (event) => {
    const link = event.target instanceof Element ? event.target.closest('[data-fw-mix-start]') : null;
    if (!link) return;
    builders.forEach((builder) => builder.start(link.getAttribute('data-fw-mix-start') || ''));
  });
})();



